import React, { createContext, useContext, useReducer, useRef, useCallback, useMemo, useEffect } from 'react';
import * as tus from 'tus-js-client';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { buildApiUrl } from '../config/api';
import logger from '../utils/logger';
import { reportUploadFailure, trackUploadEnd, trackUploadProgress, trackUploadStart } from '../lib/uploadTelemetry';
import { resilientTusOptions } from '../lib/tusResilience';
import { createAttachedFileInput, detachFileInput } from '../lib/filePicker';
import { trackEvent } from '../lib/analytics';
import { captureVideoFrame, hasRecapFrame, saveRecapFrame } from '../utils/recapFrames';
import { emitCoachFeedbackVideoSettled } from '../lib/coachFeedbackEvents';
import { canRetryPlaceholder, coachFeedbackLanded, confirmWithRetry } from '../lib/confirmWithRetry';

const BackgroundUploadContext = createContext(null);

// Le module wake lock n'est chargé qu'au premier envoi : inutile au démarrage
// (budget du chunk principal, cf. .size-limit.json). Une seule promesse : les
// appels s'appliquent dans l'ordre où ils ont été faits.
let wakeLockModule = null;
const toggleUploadWakeLock = (active) => {
  if (!active && !wakeLockModule) return; // jamais demandé : rien à libérer
  if (!wakeLockModule) wakeLockModule = import('../lib/uploadWakeLock');
  wakeLockModule.then((m) => m.setUploadWakeLock(active)).catch(() => {});
};

// Action types
const START_UPLOAD = 'START_UPLOAD';
const START_SELECTION = 'START_SELECTION';
const CANCEL_SELECTION = 'CANCEL_SELECTION';
const SET_PROGRESS = 'SET_PROGRESS';
const SET_STATUS = 'SET_STATUS';
const SET_VIDEO_ID = 'SET_VIDEO_ID';
const SET_ERROR = 'SET_ERROR';
const DISMISS = 'DISMISS';

function uploadReducer(state, action) {
  switch (action.type) {
    case START_SELECTION:
      return {
        ...state,
        [action.payload.id]: {
          id: action.payload.id,
          exerciseInfo: action.payload.exerciseInfo,
          setInfo: action.payload.setInfo,
          status: 'SELECTING',
          progress: 0,
          error: null,
          videoId: null,
        },
      };
    case CANCEL_SELECTION:
      if (!state[action.payload.id] || state[action.payload.id].status !== 'SELECTING') return state;
      {
        const { [action.payload.id]: _, ...rest } = state;
        return rest;
      }
    case START_UPLOAD:
      return {
        ...state,
        [action.payload.id]: {
          ...(state[action.payload.id] || {}),
          id: action.payload.id,
          exerciseInfo: action.payload.exerciseInfo || state[action.payload.id]?.exerciseInfo,
          setInfo: action.payload.setInfo || state[action.payload.id]?.setInfo,
          // `kind` distingue l'upload élève (défaut) du retour vidéo coach, qui
          // n'a ni exercice ni série mais un libellé propre.
          kind: action.payload.kind || state[action.payload.id]?.kind || 'student_set',
          label: action.payload.label || state[action.payload.id]?.label || null,
          status: 'PENDING',
          progress: 0,
          error: null,
          videoId: null,
        },
      };
    case SET_PROGRESS:
      if (!state[action.payload.id]) return state;
      // TUS émet onProgress des dizaines de fois par seconde et par upload.
      // Sans ce court-circuit, chaque événement recrée l'état et re-rend le
      // provider + tous ses consommateurs, même quand le pourcentage arrondi
      // n'a pas bougé — avec plusieurs uploads simultanés, le thread principal
      // est saturé par l'affichage et n'alimente plus l'envoi lui-même.
      if (state[action.payload.id].progress === action.payload.progress) return state;
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          progress: action.payload.progress,
        },
      };
    case SET_STATUS:
      if (!state[action.payload.id]) return state;
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          status: action.payload.status,
        },
      };
    case SET_VIDEO_ID:
      if (!state[action.payload.id]) return state;
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          videoId: action.payload.videoId,
        },
      };
    case SET_ERROR:
      if (!state[action.payload.id]) return state;
      return {
        ...state,
        [action.payload.id]: {
          ...state[action.payload.id],
          error: action.payload.error,
          status: 'FAILED',
        },
      };
    case DISMISS: {
      const { [action.payload.id]: _, ...rest } = state;
      return rest;
    }
    default:
      return state;
  }
}

export function BackgroundUploadProvider({ children }) {
  const [uploads, dispatch] = useReducer(uploadReducer, {});
  // Écran maintenu allumé tant qu'un envoi est en cours (lib/uploadWakeLock.js).
  // Dépend d'un booléen : l'effet ne tourne qu'au changement d'état, pas à
  // chaque tick de progression.
  const isUploading = Object.values(uploads).some((u) =>
    u.status === 'PENDING' || u.status === 'UPLOADING' || u.status === 'UPLOADED_RAW');
  useEffect(() => {
    toggleUploadWakeLock(isUploading);
  }, [isUploading]);
  useEffect(() => () => toggleUploadWakeLock(false), []);
  // Miroir de `uploads` pour les lectures à identité stable (getSessionUploadFiles).
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;
  const tusInstancesRef = useRef({}); // Map of upload id -> tus.Upload instance
  const fileRefs = useRef({}); // Map of upload id -> File object (for retry)
  const metadataRefs = useRef({}); // Map of upload id -> TUS metadata (for retry)
  const callbackRefs = useRef({}); // Map of upload id -> onSuccess callback
  const fileInputRefs = useRef({}); // Map of upload id -> <input> element (survives modal unmount)
  const coachParamsRefs = useRef({}); // Map of upload id -> params du retour vidéo coach (pour le réessai)
  const recapCaptureStartedRef = useRef(new Set()); // Ids déjà lancés pour la capture du récap (indépendant du stockage)
  const { getAuthToken } = useAuth();

  const startBackgroundUpload = useCallback(async (file, tusMetadata, exerciseInfo, setInfo, onSuccess) => {
    const id = `${exerciseInfo.exerciseIndex}-${setInfo.setIndex}`;

    // Store file, metadata, and callback for potential retry
    fileRefs.current[id] = file;
    callbackRefs.current[id] = onSuccess;

    dispatch({ type: START_UPLOAD, payload: { id, exerciseInfo, setInfo } });

    // Image de fond du récap partageable : prise ici, sur le téléphone, au
    // moment du choix de la vidéo — sans attendre le traitement serveur.
    // Jamais bloquant pour l'envoi, et une seule fois par série : le garde-fou
    // en mémoire (recapCaptureStartedRef) couvre la capture déjà en cours (un
    // réessai pendant qu'elle tourne ne la relance pas, sans dépendre du
    // stockage), et hasRecapFrame couvre les réessais après coup. La clé du
    // garde-fou inclut l'id de séance : le provider vit pour toute la durée de
    // l'appli, donc un simple id exercice-série se recroiserait d'une séance
    // à l'autre (ex. série 0-0 d'une nouvelle séance le lendemain, sans
    // rechargement) et bloquerait sa capture à tort. Tout throw synchrone ici
    // est intercepté : la capture ne doit jamais faire échouer l'envoi.
    try {
      const recapSessionId = exerciseInfo.sessionId;
      const recapCaptureKey = `${recapSessionId}/${id}`;
      if (
        recapSessionId &&
        !recapCaptureStartedRef.current.has(recapCaptureKey) &&
        !hasRecapFrame(recapSessionId, exerciseInfo.exerciseIndex, setInfo.setIndex)
      ) {
        recapCaptureStartedRef.current.add(recapCaptureKey);
        // Capture ratée (null ou rejet) : la clé est libérée, pour qu'une
        // nouvelle vidéo choisie pour cette série retente la capture.
        const releaseCaptureKey = () => recapCaptureStartedRef.current.delete(recapCaptureKey);
        captureVideoFrame(file)
          .then((dataUrl) => {
            if (!dataUrl) {
              releaseCaptureKey();
              return;
            }
            saveRecapFrame(recapSessionId, {
              exerciseIndex: exerciseInfo.exerciseIndex,
              setIndex: setInfo.setIndex,
              exerciseName: exerciseInfo.exerciseName || '',
              dataUrl,
              source: 'video'
            });
          })
          .catch((error) => {
            releaseCaptureKey();
            logger.debug('Recap frame skipped:', error);
          });
      }
    } catch (error) {
      logger.debug('Recap frame skipped:', error);
    }

    let stage = 'placeholder';
    // Annulé (abortUpload/dismissUpload) ou remplacé par une autre vidéo pour la
    // même série pendant la création du placeholder : on s'arrête sans rien toucher.
    const isReplaced = () => fileRefs.current[id] !== file;
    try {
      let token; // relu à chaque essai ; le dernier sert aussi à l'envoi TUS

      // 1. Create placeholder video record
      // Réessais + attente du retour au premier plan, seulement quand le serveur
      // retrouve la ligne de la série (voir canRetryPlaceholder) : iOS perd
      // parfois la réponse alors que la ligne est déjà créée (KAIYLO-FRONTEND-55).
      const response = await confirmWithRetry(async () => {
        token = await getAuthToken();
        return axios.post(
          buildApiUrl('/videos'),
          tusMetadata,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
        );
      }, { attempts: canRetryPlaceholder(tusMetadata) ? 3 : 1, isCancelled: isReplaced });
      if (isReplaced()) return id;

      const { videoId, bucket, objectName, uploadUrl } = response.data;
      stage = 'tus';

      dispatch({ type: SET_VIDEO_ID, payload: { id, videoId } });
      dispatch({ type: SET_STATUS, payload: { id, status: 'UPLOADING' } });

      // Store full upload metadata for retry
      const fullUploadMeta = { videoId, bucket, objectName, uploadUrl };
      metadataRefs.current[id] = fullUploadMeta;

      // 2. Start TUS upload
      const upload = new tus.Upload(file, {
        endpoint: uploadUrl,
        ...resilientTusOptions(getAuthToken, { token, headers: { 'x-upsert': 'true' } }),
        metadata: {
          bucketName: bucket,
          objectName: objectName,
          contentType: file.type,
          cacheControl: '3600',
        },
        chunkSize: 6 * 1024 * 1024,
        onError: (err) => {
          logger.error('Background TUS Upload failed:', err);
          reportUploadFailure(id, 'tus', err);
          dispatch({ type: SET_ERROR, payload: { id, error: 'Upload failed: ' + err.message } });
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          trackUploadProgress(id, bytesUploaded);
          const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
          dispatch({ type: SET_PROGRESS, payload: { id, progress: percentage } });
        },
        onSuccess: async () => {
          dispatch({ type: SET_STATUS, payload: { id, status: 'UPLOADED_RAW' } });
          // Confirm upload to backend
          try {
            // Réessais + attente du retour au premier plan : voir lib/confirmWithRetry.js.
            // Annulé (abortUpload/dismissUpload) ou remplacé (nouvelle vidéo pour la
            // même série) pendant la confirmation : l'instance TUS du registre n'est
            // plus la nôtre. On n'y touche plus, sinon on marquerait READY la
            // NOUVELLE entrée avec l'ancien videoId.
            const isStale = () => tusInstancesRef.current[id] !== upload;
            const confirmResponse = await confirmWithRetry(async () => {
              const freshToken = await getAuthToken();
              return axios.patch(
                buildApiUrl(`/videos/${videoId}`),
                {
                  status: 'UPLOADED_RAW',
                  raw_storage_path: objectName,
                  size_bytes_raw: file.size,
                  duration_ms: 0,
                },
                {
                  headers: { Authorization: `Bearer ${freshToken}` },
                  // Une requête bloquée laisserait l'upload (et l'écran allumé) en
                  // « Finalisation » indéfiniment ; le réessai couvre le timeout.
                  timeout: 30000,
                }
              );
            }, { isCancelled: isStale });
            if (isStale()) return;
            trackUploadEnd(id);
            dispatch({ type: SET_STATUS, payload: { id, status: 'READY' } });
            trackEvent('video_uploaded', { size_mb: Math.round(file.size / (1024 * 1024)) });

            // Call the onSuccess callback with the same payload shape as the modal used to
            const cb = callbackRefs.current[id];
            if (cb) {
              cb({
                file: 'uploaded',
                videoId: videoId,
                status: 'READY',
                videoUrl: confirmResponse.data?.rawVideoUrl || null,
                exerciseInfo,
                setInfo,
              });
            }
          } catch (confirmErr) {
            // Annulé ou remplacé : ni échec rapporté, ni état d'erreur sur la nouvelle entrée.
            if (tusInstancesRef.current[id] !== upload) return;
            logger.error('Failed to confirm background upload:', confirmErr);
            reportUploadFailure(id, 'confirm', confirmErr);
            dispatch({ type: SET_ERROR, payload: { id, error: 'Upload completed but confirmation failed.' } });
          }
        },
      });

      tusInstancesRef.current[id] = upload;
      trackUploadStart(id, { kind: 'student_set', videoId, file });
      upload.start();
    } catch (err) {
      if (err?.cancelled) return id;
      logger.error('Background upload flow failed:', err);
      reportUploadFailure(id, stage, err, { kind: 'student_set', file });
      dispatch({
        type: SET_ERROR,
        payload: { id, error: err instanceof Error ? err.message : 'Upload initialization failed' },
      });
    }

    return id;
  }, [getAuthToken]);

  /**
   * Retour vidéo du coach, envoyé en arrière-plan.
   *
   * Avant, l'envoi vivait dans VideoDetailModal : fermer la fenêtre tuait
   * l'upload, et le coach n'avait aucune progression à l'écran. Il devait donc
   * attendre devant sa modale, sans rien voir bouger, pour chaque élève.
   * Ici l'envoi survit à la fermeture et s'affiche dans BackgroundUploadIndicator,
   * ce qui laisse le coach enchaîner ses retours.
   *
   * Même séquence que côté élève, mais sur les endpoints coach :
   * placeholder → TUS vers videos_raw → PATCH .../feedback (crée la ligne
   * coach_feedback_videos et lance le worker).
   */
  const startCoachFeedbackUpload = useCallback(async (params) => {
    const { id, videoId, studentName, file, duration, comment, isFirst, batchSize } = params;
    coachParamsRefs.current[id] = params;
    fileRefs.current[id] = file;

    dispatch({ type: START_UPLOAD, payload: { id, kind: 'coach_feedback', label: studentName || null } });

    let stage = 'placeholder';
    try {
      const token = await getAuthToken();
      const ext = (file.name.split('.').pop() || 'webm').toLowerCase();

      const { data } = await axios.post(
        buildApiUrl(`/workout-sessions/videos/${videoId}/feedback-video/placeholder`),
        { fileExtension: ext },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { bucket, objectName, uploadUrl } = data.data;
      stage = 'tus';
      trackUploadStart(id, { kind: 'coach_feedback', videoId, file });

      // Retours vidéo déjà présents, relevés pendant l'envoi : sert à reconnaître
      // une confirmation arrivée malgré une réponse perdue (voir plus bas).
      const listFeedbackRows = async () => {
        const t = await getAuthToken();
        const res = await axios.get(buildApiUrl(`/videos/${videoId}`), { headers: { Authorization: `Bearer ${t}` } });
        return res?.data?.video?.coach_feedback_videos || [];
      };
      const knownIds = listFeedbackRows().then((rows) => rows.map((r) => r.id), () => null);

      dispatch({ type: SET_VIDEO_ID, payload: { id, videoId } });
      dispatch({ type: SET_STATUS, payload: { id, status: 'UPLOADING' } });

      await new Promise((resolve, reject) => {
        const upload = new tus.Upload(file, {
          endpoint: uploadUrl,
          ...resilientTusOptions(getAuthToken, { token, headers: { 'x-upsert': 'true' } }),
          metadata: {
            bucketName: bucket,
            objectName,
            // MediaRecorder produit "video/webm;codecs=vp9,opus", refusé en 415
            // par Supabase Storage : on n'envoie que le type de base.
            contentType: (file.type || 'video/webm').split(';')[0],
            cacheControl: '3600',
          },
          chunkSize: 6 * 1024 * 1024,
          onError: reject,
          onProgress: (bytesUploaded, bytesTotal) => {
            trackUploadProgress(id, bytesUploaded);
            dispatch({ type: SET_PROGRESS, payload: { id, progress: Math.round((bytesUploaded / bytesTotal) * 100) } });
          },
          onSuccess: resolve,
        });
        tusInstancesRef.current[id] = upload;
        upload.start();
      });

      dispatch({ type: SET_STATUS, payload: { id, status: 'UPLOADED_RAW' } });
      stage = 'confirm';

      // Attente du retour au premier plan (iOS coupe le réseau en arrière-plan),
      // mais UN seul envoi : ce PATCH insère une ligne coach_feedback_videos à
      // chaque appel (non idempotent) — un réessai pourrait dupliquer le retour.
      try {
        await confirmWithRetry(async () => {
          const freshToken = await getAuthToken();
          return axios.patch(
            buildApiUrl(`/workout-sessions/videos/${videoId}/feedback`),
            {
              coach_feedback_video_raw_path: objectName,
              coach_feedback_video_duration: duration,
              feedback: comment || undefined,
              // Une seule carte de chat par lot : seule la première vidéo la crée.
              coach_feedback_is_first: isFirst,
              coach_feedback_batch_size: batchSize,
            },
            { headers: { Authorization: `Bearer ${freshToken}` } }
          );
        }, { attempts: 1 });
      } catch (err) {
        // Sans réponse (réseau coupé), le PATCH a pu aboutir : on vérifie avant d'afficher l'échec.
        if (err?.response || !(await coachFeedbackLanded(listFeedbackRows, await knownIds, duration))) throw err;
        trackEvent('coach_feedback_confirm_recovered', { video_id: videoId });
      }

      trackUploadEnd(id);
      dispatch({ type: SET_STATUS, payload: { id, status: 'READY' } });
      emitCoachFeedbackVideoSettled(videoId, true);
    } catch (err) {
      logger.error('Coach feedback background upload failed:', err);
      reportUploadFailure(id, stage, err, { kind: 'coach_feedback', videoId, file });
      emitCoachFeedbackVideoSettled(videoId, false);
      dispatch({
        type: SET_ERROR,
        payload: { id, error: err instanceof Error ? err.message : 'Envoi du retour vidéo échoué' },
      });
    }

    return id;
  }, [getAuthToken]);

  // Start file selection: creates SELECTING entry, triggers file picker, handles cancellation
  // This lives in the context so the <input> and callbacks survive modal unmount.
  const triggerBackgroundFileSelect = useCallback((exerciseInfo, setInfo, onSuccess, tusMetadataTemplate, maxFileSize) => {
    const id = `${exerciseInfo.exerciseIndex}-${setInfo.setIndex}`;

    // Create SELECTING entry
    dispatch({ type: START_SELECTION, payload: { id, exerciseInfo, setInfo } });
    callbackRefs.current[id] = onSuccess;

    // Clean up previous input if any
    detachFileInput(fileInputRefs.current[id]);

    // Le champ doit vivre dans le document : Chrome (Android) ignore en silence
    // `.click()` sur un input détaché — voir src/lib/filePicker.js.
    const input = createAttachedFileInput({ accept: 'video/*' });
    fileInputRefs.current[id] = input;

    let fileSelected = false;

    input.onchange = (e) => {
      fileSelected = true;
      const file = e.target.files[0];
      if (file) {
        if (maxFileSize && file.size > maxFileSize) {
          alert(`❌ Vidéo trop volumineuse ! (Max ${Math.round(maxFileSize / 1024 / 1024)} Mo)`);
          dispatch({ type: CANCEL_SELECTION, payload: { id } });
          return;
        }
        logger.debug('📁 File selected via context picker:', { name: file.name, size: file.size, type: file.type });

        // Transition SELECTING → start upload
        fileRefs.current[id] = file;
        startBackgroundUpload(file, tusMetadataTemplate, exerciseInfo, setInfo, onSuccess);
      } else {
        // No file in the event — treat as cancellation
        dispatch({ type: CANCEL_SELECTION, payload: { id } });
      }
    };

    // Detect picker cancellation via visibilitychange
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Page became visible again (picker closed). Wait a bit for onchange to fire.
        setTimeout(() => {
          if (!fileSelected) {
            logger.debug('📁 File picker cancelled (no file selected)');
            dispatch({ type: CANCEL_SELECTION, payload: { id } });
          }
          document.removeEventListener('visibilitychange', handleVisibility);
        }, 500);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Also detect via focus for desktop browsers where visibilitychange may not fire
    const handleFocus = () => {
      setTimeout(() => {
        if (!fileSelected) {
          logger.debug('📁 File picker cancelled via focus (no file selected)');
          dispatch({ type: CANCEL_SELECTION, payload: { id } });
        }
        window.removeEventListener('focus', handleFocus);
      }, 500);
    };
    window.addEventListener('focus', handleFocus);

    // Trigger the file picker
    input.click();

    return id;
  }, [startBackgroundUpload]);

  const cancelSelection = useCallback((id) => {
    dispatch({ type: CANCEL_SELECTION, payload: { id } });
    if (fileInputRefs.current[id]) {
      detachFileInput(fileInputRefs.current[id]);
      delete fileInputRefs.current[id];
    }
  }, []);

  const getUploadForSet = useCallback((exerciseIndex, setIndex) => {
    const id = `${exerciseIndex}-${setIndex}`;
    return uploads[id] || null;
  }, [uploads]);

  // Lecture seule, pour le récap : les fichiers encore en mémoire d'une séance.
  // `fileRefs` est rangé par « exercice-série » sans la séance : on ne rend que
  // ceux dont l'upload porte bien cette séance.
  // Identité stable (lecture via `uploadsRef`) : les ticks de progression ne
  // doivent pas refaire le rendu ni redessiner le récap. Une entrée encore en
  // SELECTING est ignorée : son `fileRefs` peut être celui d'une autre séance.
  const getSessionUploadFiles = useCallback((sessionId) => {
    if (!sessionId) return [];
    return Object.values(uploadsRef.current)
      .filter((u) => u?.kind !== 'coach_feedback' && u?.status !== 'SELECTING' && u?.exerciseInfo?.sessionId === sessionId && fileRefs.current[u.id])
      .map((u) => ({ exerciseIndex: u.exerciseInfo.exerciseIndex, setIndex: u.setInfo?.setIndex, file: fileRefs.current[u.id] }))
      .filter((f) => Number.isFinite(f.exerciseIndex) && Number.isFinite(f.setIndex));
  }, []);

  const abortUpload = useCallback((id) => {
    // Abort the TUS upload if active
    if (tusInstancesRef.current[id]) {
      tusInstancesRef.current[id].abort();
    }
    // Annulation volontaire : pas un échec, on retire juste l'upload du registre.
    trackUploadEnd(id);
    // Clean up all refs
    delete tusInstancesRef.current[id];
    delete fileRefs.current[id];
    delete metadataRefs.current[id];
    delete callbackRefs.current[id];
    delete coachParamsRefs.current[id];
    if (fileInputRefs.current[id]) {
      detachFileInput(fileInputRefs.current[id]);
      delete fileInputRefs.current[id];
    }
    dispatch({ type: DISMISS, payload: { id } });
  }, []);

  const dismissUpload = useCallback((id) => {
    // Abort any active TUS upload before cleanup
    if (tusInstancesRef.current[id]) {
      tusInstancesRef.current[id].abort();
    }
    trackUploadEnd(id);
    // Cleanup refs
    delete tusInstancesRef.current[id];
    delete fileRefs.current[id];
    delete metadataRefs.current[id];
    delete callbackRefs.current[id];
    delete coachParamsRefs.current[id];
    if (fileInputRefs.current[id]) {
      detachFileInput(fileInputRefs.current[id]);
      delete fileInputRefs.current[id];
    }
    dispatch({ type: DISMISS, payload: { id } });
  }, []);

  const retryUpload = useCallback(async (id) => {
    const file = fileRefs.current[id];
    const meta = metadataRefs.current[id];
    const upload = uploads[id];

    if (!file || !upload) return;

    // Retour vidéo coach : on rejoue tout le flux avec les paramètres d'origine
    // (placeholder + TUS + PATCH), il n'y a pas d'upload TUS reprenable ici.
    if (upload.kind === 'coach_feedback') {
      const params = coachParamsRefs.current[id];
      if (params) await startCoachFeedbackUpload(params);
      return;
    }

    // If we have an existing TUS instance with a videoId, try resuming
    if (meta && tusInstancesRef.current[id]) {
      dispatch({ type: SET_STATUS, payload: { id, status: 'UPLOADING' } });
      dispatch({ type: SET_ERROR, payload: { id, error: null } });
      // Fix error state manually since SET_ERROR forces FAILED
      dispatch({ type: SET_STATUS, payload: { id, status: 'UPLOADING' } });
      trackUploadStart(id, { kind: 'student_set', videoId: meta.videoId, file, isRetry: true });
      tusInstancesRef.current[id].start();
    } else if (upload.exerciseInfo && upload.setInfo) {
      // Full restart
      const cb = callbackRefs.current[id];
      const tusMetadata = {
        assigned_session_id: upload.exerciseInfo.sessionId,
        set_id: upload.setInfo.setId || null,
        exercise_id: upload.exerciseInfo.exerciseId,
        source: 'session_set',
        metadata: {
          exercise_name: upload.exerciseInfo.exerciseName,
          exercise_index: upload.exerciseInfo.exerciseIndex,
          set_number: upload.setInfo.setNumber,
          set_index: upload.setInfo.setIndex,
          weight: upload.setInfo.weight,
          // setInfo.weight est déjà converti en kg par WorkoutSessionExecution (videoWeightKg)
          weight_unit: upload.setInfo.weight_unit ?? 'kg',
          reps: upload.setInfo.reps,
        },
      };
      await startBackgroundUpload(file, tusMetadata, upload.exerciseInfo, upload.setInfo, cb);
    }
  }, [uploads, startBackgroundUpload, startCoachFeedbackUpload]);

  const activeUploads = useMemo(() => Object.values(uploads), [uploads]);

  // Mémoïsé : sans ça, l'objet est recréé à chaque rendu du provider et tous les
  // consommateurs re-rendent à chaque tick de progression, même ceux qui ne
  // lisent que les actions (VideoDetailModal, WorkoutVideoUploadModal).
  const value = useMemo(() => ({
    startBackgroundUpload,
    startCoachFeedbackUpload,
    triggerBackgroundFileSelect,
    cancelSelection,
    getUploadForSet,
    getSessionUploadFiles,
    activeUploads,
    dismissUpload,
    retryUpload,
    abortUpload,
  }), [
    startBackgroundUpload,
    startCoachFeedbackUpload,
    triggerBackgroundFileSelect,
    cancelSelection,
    getUploadForSet,
    getSessionUploadFiles,
    activeUploads,
    dismissUpload,
    retryUpload,
    abortUpload,
  ]);

  return (
    <BackgroundUploadContext.Provider value={value}>
      {children}
    </BackgroundUploadContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- context hook co-located with its provider (established convention; dev-only HMR hint)
export function useBackgroundUpload() {
  const context = useContext(BackgroundUploadContext);
  if (!context) {
    throw new Error('useBackgroundUpload must be used within a BackgroundUploadProvider');
  }
  return context;
}
